<template>
	<div class="today-eat-content">
		<div class="today-reco">
			<div class="today-reco-title">
				<span class="q-title">
					<span class="q-title-icon" aria-hidden="true">🍱</span>
					<span>今天吃啥</span>
				</span>
				<van-button type="primary" size="mini" @click="refreshReco" :loading="recoLoading">换一批</van-button>
			</div>
			<div class="today-reco-body" v-if="todayReco">
				<div class="reco-item">
					<span class="reco-meal reco-meal--breakfast">早餐</span>
					<div class="reco-list" v-if="mealItems(todayReco.breakfast).length">
						<div class="reco-line" v-for="(x, i) in mealItems(todayReco.breakfast)" :key="`b-${i}-${x.id}`">
							<span class="reco-name">{{ x.name }}</span>
							<span class="reco-reason" v-if="x.reason">（{{ x.reason }}）</span>
						</div>
					</div>
					<span v-else class="reco-name reco-name--empty">暂无</span>
				</div>
				<div class="reco-item">
					<span class="reco-meal reco-meal--lunch">午饭</span>
					<div class="reco-list" v-if="mealItems(todayReco.lunch).length">
						<div class="reco-line" v-for="(x, i) in mealItems(todayReco.lunch)" :key="`l-${i}-${x.id}`">
							<span class="reco-name">{{ x.name }}</span>
							<span class="reco-reason" v-if="x.reason">（{{ x.reason }}）</span>
						</div>
					</div>
					<span v-else class="reco-name reco-name--empty">暂无</span>
				</div>
				<div class="reco-item">
					<span class="reco-meal reco-meal--dinner">晚饭</span>
					<div class="reco-list" v-if="mealItems(todayReco.dinner).length">
						<div class="reco-line" v-for="(x, i) in mealItems(todayReco.dinner)" :key="`d-${i}-${x.id}`">
							<span class="reco-name">{{ x.name }}</span>
							<span class="reco-reason" v-if="x.reason">（{{ x.reason }}）</span>
						</div>
					</div>
					<span v-else class="reco-name reco-name--empty">暂无</span>
				</div>
				<div class="reco-summary" v-if="todayReco.summary">{{ todayReco.summary }}</div>
			</div>
			<div class="today-reco-body" v-else>暂无推荐</div>
		</div>

		<div class="custom-req">
			<div class="custom-req-title">
				<span class="q-title">
					<span class="q-title-icon" aria-hidden="true">📝</span>
					<span>定制需求（可选）</span>
				</span>
				<div class="custom-req-actions">
					<van-button size="mini" plain type="primary" @click="useLastRequirements">使用上次</van-button>
					<van-button size="mini" plain type="default" @click="clearRequirements">清空</van-button>
				</div>
			</div>

			<div class="tag-row">
				<van-tag
					v-for="t in presetTags"
					:key="t"
					:plain="!requirements.tags.includes(t)"
					:type="requirements.tags.includes(t) ? 'primary' : 'default'"
					round
					class="tag-pill"
					@click="toggleTag(t)"
				>
					{{ t }}
				</van-tag>
				<van-tag
					v-for="t in customTags"
					:key="`custom-${t}`"
					plain
					type="primary"
					round
					closeable
					class="tag-pill"
					@close="removeCustomTag(t)"
					@click="toggleTag(t)"
				>
					{{ t }}
				</van-tag>
				<van-tag plain type="default" round class="tag-pill tag-add" @click="promptAddTag">+ 自定义</van-tag>
			</div>

			<div class="field-block">
				<div class="field-label">补充说明</div>
				<van-field
					v-model="requirements.note"
					type="textarea"
					rows="3"
					autosize
					maxlength="300"
					show-word-limit
					placeholder="例如：家里有鸡胸肉和西兰花，想做两菜一汤；不要油炸；晚饭想吃面。"
				/>
			</div>

			<div class="field-block">
				<div class="field-label">忌口/过敏</div>
				<div class="chip-input">
					<div class="chip-list" v-if="requirements.allergens.length">
						<van-tag
							v-for="(x, i) in requirements.allergens"
							:key="`alg-${i}-${x}`"
							closeable
							type="danger"
							round
							@close="removeChip('allergens', i)"
						>
							{{ x }}
						</van-tag>
					</div>
					<van-field
						v-model="chipDraft.allergens"
						placeholder="输入后回车添加，比如：花生"
						@keydown.enter.prevent="addChip('allergens')"
					>
						<template #button>
							<van-button size="small" type="primary" plain @click="addChip('allergens')">添加</van-button>
						</template>
					</van-field>
				</div>
			</div>

			<div class="field-block">
				<div class="field-label">已有食材</div>
				<div class="chip-input">
					<div class="chip-list" v-if="requirements.haveIngredients.length">
						<van-tag
							v-for="(x, i) in requirements.haveIngredients"
							:key="`have-${i}-${x}`"
							closeable
							type="success"
							round
							@close="removeChip('haveIngredients', i)"
						>
							{{ x }}
						</van-tag>
					</div>
					<van-field
						v-model="chipDraft.haveIngredients"
						placeholder="输入后回车添加，比如：鸡胸肉"
						@keydown.enter.prevent="addChip('haveIngredients')"
					>
						<template #button>
							<van-button size="small" type="primary" plain @click="addChip('haveIngredients')">添加</van-button>
						</template>
					</van-field>
				</div>
			</div>

			<div class="field-block">
				<div class="field-label">不想吃</div>
				<div class="chip-input">
					<div class="chip-list" v-if="requirements.avoidIngredients.length">
						<van-tag
							v-for="(x, i) in requirements.avoidIngredients"
							:key="`avoid-${i}-${x}`"
							closeable
							type="warning"
							round
							@close="removeChip('avoidIngredients', i)"
						>
							{{ x }}
						</van-tag>
					</div>
					<van-field
						v-model="chipDraft.avoidIngredients"
						placeholder="输入后回车添加，比如：香菜"
						@keydown.enter.prevent="addChip('avoidIngredients')"
					>
						<template #button>
							<van-button size="small" type="primary" plain @click="addChip('avoidIngredients')">添加</van-button>
						</template>
					</van-field>
				</div>
			</div>

			<div class="meta-row">
				<div class="meta-item">
					<span class="meta-label">人数</span>
					<van-stepper v-model="requirements.people" min="1" max="10" integer />
				</div>
				<div class="meta-item meta-meals">
					<span class="meta-label">餐次</span>
					<van-checkbox-group v-model="selectedMeals" direction="horizontal">
						<van-checkbox name="breakfast">早餐</van-checkbox>
						<van-checkbox name="lunch">午饭</van-checkbox>
						<van-checkbox name="dinner">晚饭</van-checkbox>
					</van-checkbox-group>
				</div>
			</div>

			<div class="gen-row">
				<van-button block type="primary" :loading="genLoading" @click="generateWithRequirements">生成今天吃啥</van-button>
				<van-button
					v-if="noCandidatesLastTime"
					block
					plain
					type="primary"
					class="relax-btn"
					:loading="genLoading"
					@click="generateRelaxed"
				>
					放宽偏好再生成
				</van-button>
			</div>
		</div>

		<van-dialog
			v-model="showCustomTagDialog"
			title="自定义标签"
			show-cancel-button
			@confirm="confirmAddCustomTag"
		>
			<van-field v-model="customTagDraft" placeholder="例如：素食 / 少盐 / 不油腻" />
		</van-dialog>
	</div>
</template>

<script>
import { getTodayFoodRecommendation, refreshTodayFoodRecommendation } from '../util/api'
import { Notify, Dialog } from 'vant'

export default {
	name: 'TodayEat',
	data() {
		return {
			todayReco: null,
			recoLoading: false,
			genLoading: false,
			noCandidatesLastTime: false,
			presetTags: ['清淡', '高蛋白', '低脂', '儿童友好', '快手(≤20分钟)', '不辣', '一锅出'],
			requirements: {
				tags: [],
				note: '',
				allergens: [],
				haveIngredients: [],
				avoidIngredients: [],
				people: 2,
				meals: { breakfast: true, lunch: true, dinner: true },
			},
			chipDraft: {
				allergens: '',
				haveIngredients: '',
				avoidIngredients: '',
			},
			showCustomTagDialog: false,
			customTagDraft: '',
		}
	},
	computed: {
		selectedMeals: {
			get() {
				const m = this.requirements.meals || {}
				return ['breakfast', 'lunch', 'dinner'].filter((k) => Boolean(m[k]))
			},
			set(v) {
				const set = new Set(v || [])
				this.$set(this.requirements, 'meals', {
					breakfast: set.has('breakfast'),
					lunch: set.has('lunch'),
					dinner: set.has('dinner'),
				})
			},
		},
		customTags() {
			const preset = new Set(this.presetTags || [])
			const tags = Array.isArray(this.requirements?.tags) ? this.requirements.tags : []
			return tags.filter((t) => t && !preset.has(t))
		},
	},
	methods: {
		mealItems(v) {
			// Backward compatible:
			// - new schema: array [{id,name,reason}]
			// - old schema: object {id,name,reason}
			const arr = Array.isArray(v) ? v : (v ? [v] : []);
			return arr
				.map((x) => ({
					id: x?.id,
					name: x?.name,
					reason: x?.reason,
				}))
				.filter((x) => Boolean(x.name));
		},
		getTodayReco() {
			getTodayFoodRecommendation().then((res) => {
				const content = res?.data?.data?.content
				this.$set(this, 'todayReco', content || null)
			})
		},
		refreshReco() {
			this.recoLoading = true;
			refreshTodayFoodRecommendation({}).then((res) => {
				const content = res?.data?.data?.content
				this.$set(this, 'todayReco', content || null)
			}).finally(() => {
				this.recoLoading = false;
			})
		},

		saveRequirements() {
			try {
				localStorage.setItem('todayEat.requirements', JSON.stringify(this.requirements))
			} catch (_) {
				// ignore storage errors (private mode / quota)
			}
		},
		useLastRequirements() {
			try {
				const raw = localStorage.getItem('todayEat.requirements')
				if (!raw) {
					Notify({ type: 'primary', message: '暂无上次记录' })
					return
				}
				const parsed = JSON.parse(raw)
				if (parsed && typeof parsed === 'object') {
					this.requirements = Object.assign({}, this.requirements, parsed)
					Notify({ type: 'success', message: '已恢复上次需求' })
				}
			} catch (e) {
				Notify({ type: 'danger', message: '恢复失败' })
			}
		},
		clearRequirements() {
			Dialog.confirm({ title: '清空需求', message: '确定清空当前输入吗？' })
				.then(() => {
					this.requirements = {
						tags: [],
						note: '',
						allergens: [],
						haveIngredients: [],
						avoidIngredients: [],
						people: 2,
						meals: { breakfast: true, lunch: true, dinner: true },
					}
					this.chipDraft = { allergens: '', haveIngredients: '', avoidIngredients: '' }
					this.noCandidatesLastTime = false
					try {
						localStorage.removeItem('todayEat.requirements')
					} catch (_) {
						// ignore
					}
				})
				.catch(() => { })
		},
		toggleTag(t) {
			const set = new Set(this.requirements.tags || [])
			if (set.has(t)) set.delete(t)
			else set.add(t)
			this.$set(this.requirements, 'tags', Array.from(set))
			this.saveRequirements()
		},
		promptAddTag() {
			this.customTagDraft = ''
			this.showCustomTagDialog = true
		},
		confirmAddCustomTag() {
			const v = String(this.customTagDraft || '').trim()
			if (!v) return
			const set = new Set(this.requirements.tags || [])
			set.add(v)
			this.$set(this.requirements, 'tags', Array.from(set))
			this.saveRequirements()
			this.showCustomTagDialog = false
		},
		removeCustomTag(t) {
			const set = new Set(this.requirements.tags || [])
			set.delete(t)
			this.$set(this.requirements, 'tags', Array.from(set))
			this.saveRequirements()
		},
		normalizeChip(v) {
			return String(v || '').trim().replace(/\s+/g, ' ')
		},
		addChip(field) {
			const v = this.normalizeChip(this.chipDraft[field])
			if (!v) return
			const list = Array.isArray(this.requirements[field]) ? this.requirements[field] : []
			if (!list.includes(v)) {
				this.$set(this.requirements, field, [...list, v])
				this.saveRequirements()
			}
			this.$set(this.chipDraft, field, '')
		},
		removeChip(field, idx) {
			const list = Array.isArray(this.requirements[field]) ? this.requirements[field] : []
			this.$set(this.requirements, field, list.filter((_, i) => i !== idx))
			this.saveRequirements()
		},
		async generateWithRequirements() {
			this.genLoading = true
			this.noCandidatesLastTime = false
			this.saveRequirements()
			try {
				const res = await refreshTodayFoodRecommendation({ requirements: this.requirements })
				const content = res?.data?.data?.content
				const noCandidates = Boolean(res?.data?.data?.noCandidates)
				this.noCandidatesLastTime = noCandidates
				if (noCandidates) {
					Notify({ type: 'warning', message: '条件太严格，没有可用候选' })
				} else if (res?.data?.data?.source === 'random') {
					Notify({ type: 'primary', message: 'AI 不可用，已使用随机推荐' })
				}
				this.$set(this, 'todayReco', content || null)
			} catch (e) {
				Notify({ type: 'danger', message: '生成失败，请稍后重试' })
			} finally {
				this.genLoading = false
			}
		},
		async generateRelaxed() {
			this.genLoading = true
			this.saveRequirements()
			try {
				const res = await refreshTodayFoodRecommendation({ requirements: this.requirements, relax: true })
				const content = res?.data?.data?.content
				const noCandidates = Boolean(res?.data?.data?.noCandidates)
				this.noCandidatesLastTime = noCandidates
				if (noCandidates) {
					Notify({ type: 'warning', message: '仍无候选，请减少“不想吃”或检查忌口' })
				} else if (res?.data?.data?.source === 'random') {
					Notify({ type: 'primary', message: 'AI 不可用，已使用随机推荐' })
				}
				this.$set(this, 'todayReco', content || null)
			} catch (e) {
				Notify({ type: 'danger', message: '生成失败，请稍后重试' })
			} finally {
				this.genLoading = false
			}
		},
	},
	mounted() {
		this.useLastRequirements();
		this.getTodayReco();
	}
}
</script>

<style scoped>
.today-eat-content {
	/* 在 App.vue 固定高度的 main-content 内滚动，避免内容溢出压到 menubar */
	padding: 16px 16px calc(12px + env(safe-area-inset-bottom));
	display: flex;
	flex-direction: column;
	align-items: stretch;
	box-sizing: border-box;
	gap: 12px;
	width: 100%;
	height: 100%;
	overflow-y: auto;
	overflow-x: hidden;
	-webkit-overflow-scrolling: touch;
	overscroll-behavior: contain;
}

.custom-req {
	/* 让卡片自动吃到父容器的 content-box（避免 width:100% 直接按 375 计算） */
	/* Q版：更软萌的卡片与渐变描边 */
	border: 2px solid transparent;
	border-radius: 18px;
	padding: 14px 14px 12px;
	background:
		linear-gradient(rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.92)) padding-box,
		linear-gradient(135deg, rgba(52, 195, 255, 0.35), rgba(255, 107, 132, 0.28), rgba(45, 212, 191, 0.25)) border-box;
	box-shadow: 0 10px 24px rgba(17, 24, 39, 0.08);
	position: relative;
}
.custom-req-title {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 10px;
	min-width: 0;
	flex-wrap: wrap;
	position: relative;
	z-index: 1;
}
.custom-req-actions {
	display: inline-flex;
	gap: 8px;
	flex-shrink: 0;
	flex-wrap: wrap;
}
.tag-row {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-top: 10px;
}
.tag-pill {
	padding: 7px 12px;
	font-weight: 700;
	border-width: 1px;
	box-shadow: 0 8px 16px rgba(52, 195, 255, 0.10);
}
.tag-add {
	border-style: dashed;
	background: rgba(255, 255, 255, 0.65);
}
.field-block {
	margin-top: 12px;
}
.field-label {
	font-size: 12px;
	color: rgba(107, 114, 128, 0.95);
	font-weight: 700;
	margin-bottom: 6px;
}
.custom-req /deep/ .van-field__control {
	font-weight: 600;
	min-width: 0;
	flex: 1 1 auto;
}
.custom-req /deep/ .van-field {
	border-radius: 14px;
	background: rgba(255, 255, 255, 0.86);
	box-shadow: 0 8px 16px rgba(17, 24, 39, 0.05);
}
.custom-req /deep/ .van-field__body {
	display: flex;
	align-items: center;
	min-width: 0;
}
.custom-req /deep/ .van-field__button {
	flex: 0 0 auto;
}
.custom-req /deep/ .van-field__button .van-button {
	min-width: 56px;
}
.custom-req /deep/ .van-button--primary {
	background: linear-gradient(180deg, rgba(52, 195, 255, 1) 0%, rgba(110, 231, 255, 1) 100%);
	border: none;
	font-weight: 800;
}
.custom-req /deep/ .van-button--plain.van-button--primary {
	border: 1px solid rgba(52, 195, 255, 0.45);
	color: rgba(14, 165, 233, 0.95);
	background: rgba(255, 255, 255, 0.7);
	font-weight: 800;
}
.chip-input {
	display: flex;
	flex-direction: column;
	gap: 8px;
}
.chip-list {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}
.meta-row {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 12px;
	margin-top: 12px;
}
.meta-item {
	display: flex;
	align-items: center;
	gap: 10px;
}
.meta-label {
	font-size: 12px;
	color: rgba(107, 114, 128, 0.95);
	font-weight: 700;
}
.meta-meals {
	flex: 1;
	min-width: 220px;
}
.gen-row {
	margin-top: 12px;
	display: flex;
	flex-direction: column;
	gap: 10px;
}
.relax-btn {
	border-style: dashed;
}

.today-reco {
	/* 同 custom-req：避免 width:100% 导致无视 padding 的计算差异 */
	border: 2px solid transparent;
	border-radius: 16px;
	padding: 14px 14px 12px;
	background: linear-gradient(rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.92)) padding-box,
		linear-gradient(135deg, rgba(52, 195, 255, 0.45), rgba(255, 107, 132, 0.35), rgba(45, 212, 191, 0.35)) border-box;
	box-shadow: 0 10px 24px rgba(17, 24, 39, 0.08);
	position: relative;
}
.today-reco-title {
	display: flex;
	justify-content: space-between;
	align-items: center;
	font-size: 14px;
	font-weight: 700;
	letter-spacing: 0.2px;
	min-width: 0;
	flex-wrap: wrap;
	gap: 10px;
}
.q-title {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding-left: 0;
	min-width: 0;
}
.q-title > span:last-child {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.q-title-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 22px;
	height: 22px;
	border-radius: 8px;
	background: rgba(52, 195, 255, 0.16);
	box-shadow: 0 6px 14px rgba(52, 195, 255, 0.16);
	transform: rotate(-6deg);
}
.today-reco-body {
	margin-top: 8px;
	font-size: 12px;
	display: flex;
	flex-direction: column;
	gap: 6px;
}
.reco-item {
	display: flex;
	align-items: center;
	gap: 6px;
	flex-wrap: wrap;
}
.reco-list {
	display: flex;
	flex-direction: column;
	gap: 4px;
	flex: 1 1 100%;
	padding-left: 2px;
}
.reco-line {
	display: flex;
	align-items: baseline;
	gap: 6px;
	flex-wrap: wrap;
}
.reco-meal {
	color: rgba(107, 114, 128, 0.95);
	border: 1px solid rgba(15, 23, 42, 0.08);
	padding: 2px 8px;
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.75);
	display: inline-flex;
	align-items: center;
	gap: 4px;
}
.reco-meal::before {
	content: "🍡";
	font-size: 12px;
	line-height: 1;
}
.reco-meal--breakfast::before {
	content: "🥐";
}
.reco-meal--lunch::before {
	content: "🍜";
}
.reco-meal--dinner::before {
	content: "🍲";
}
.reco-name {
	color: #0ea5e9;
	font-weight: 700;
	position: relative;
}
.reco-name--empty {
	color: rgba(107, 114, 128, 0.9);
	font-weight: 600;
}
.reco-name::after {
	content: "";
	position: absolute;
	left: 0;
	bottom: -2px;
	width: 100%;
	height: 6px;
	border-radius: 999px;
	background: linear-gradient(90deg, rgba(52, 195, 255, 0.26), rgba(255, 107, 132, 0.18));
	opacity: 0.8;
	z-index: -1;
	transform: translateY(2px);
}
.reco-name--empty::after {
	display: none;
}
.reco-summary {
	color: rgba(107, 114, 128, 0.95);
	padding-top: 2px;
}
</style>

